import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Popup, Button } from 'devextreme-react';
import { TextBox, Button as TextBoxButton } from 'devextreme-react/text-box';
import { Position } from 'devextreme-react/popup';
import { randomStr } from 'utils/random-str';
import { useScreenSize } from 'utils/media-query';
import styles from './search-dropdown.module.scss';
import { useDeviceType } from 'hooks';
import { IPartner } from 'types/datatypes/IPartner';

interface ChildRef {
  search: (value: string) => void;
  moveDown: () => void;
  moveUp: () => void;
  focus: () => void;
  selectCurrent: () => void;
  loadData: () => void;
}

type PopupMode = 'dropdown' | 'fullscreen' | 'popup';

export interface SearchDropdownProps {
  minSearchLength?: number;
  searchTimeout?: number;
  placeholder?: string;
  saveSearchValueOnLeave?: boolean;
  width?: string | number;
  dropdownWidth?: string | number;
  dropdownHeight?: string | number;
  popupMode?: PopupMode;
  id?: string;
  text?: string;
  value: unknown;
  onSelect: (data: IPartner) => void;
  onEdit?: (value: unknown) => void;
  onDetailButton?: () => void;
  onOpenDocButton?: () => void;
  calcSearchValue?: (textValue: string) => string;
  children: React.ReactElement;
  readOnly?: boolean;
  validator?: React.ReactNode;
  detailComponent?: React.ReactNode;
  top?: boolean;
  [key: string]: unknown;
}

export const SearchDropdown = ({

  minSearchLength = 1,
  searchTimeout = 0,
  placeholder = '...',
  saveSearchValueOnLeave = true,
  width = '100%',
  dropdownWidth = '75%',
  dropdownHeight = 300,
  popupMode: popupModeProp = 'dropdown',
  id = '',
  ...props
}: SearchDropdownProps) => {
  const [ showContent, setShowContent ] = useState(false);

  const { isDesktop } = useDeviceType();
  const popupMode: PopupMode = isDesktop ? popupModeProp : 'fullscreen';
  const useAdditionalSearch = popupMode !== 'dropdown';
  const text = useMemo(() => props.text ?? '', [ props.text ]);

  const calcSearchValue = useCallback((textValue: string) => (
    props.calcSearchValue?.(textValue) ?? ''
  ), [ props ]);

  const childRef = useRef<ChildRef>({} as ChildRef);
  const popupTextBoxRef = useRef<TextBox>(null);
  const textBoxRef = useRef<TextBox>(null);
  const popupRef = useRef<Popup>(null);
  const textBoxId = useRef(id || randomStr('id_'));
  const popupContentRef = useRef<HTMLDivElement>(null);
  const searchValue = useRef(text);
  const delayDebounceFn = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isXSmall } = useScreenSize();
  const readOnly = props?.readOnly === true;

  useEffect(() => {
    searchValue.current = calcSearchValue(text);
    textBoxRef.current?.instance.option('value', text);
  }, [ text, calcSearchValue ]);

  const shouldContentShow = () => (searchValue.current?.length ?? 0) >= minSearchLength;

  const leaveSearch = () => {
    textBoxRef.current?.instance.option('showClearButton', false);
    textBoxRef.current?.instance.option('value', text);
    if (!saveSearchValueOnLeave) searchValue.current = calcSearchValue(text);
    textBoxRef.current?.instance.blur();
  };

  const enterSearch = () => {
    const { instance } = textBoxRef.current!;
    instance.option('showClearButton', true);
    if (!useAdditionalSearch) instance.option('value', searchValue.current);
  };

  const leaveComponent = () => {
    leaveSearch();
    popupRef.current?.instance.hide();
  };

  const showPopup = () => {
    popupRef.current!.instance.show();
    enterSearch();
    textBoxRef.current!.instance.focus();
    childRef.current.search(searchValue.current);
  };

  const handleSelectionChange = (data: IPartner) => {
    props.onSelect(data);
    textBoxRef.current?.instance.reset();
    leaveComponent();
  };

  const handleSearchTextChange = ({ event }: { event?: Event & { target: HTMLInputElement } }) => {
    const inputText = event?.target.value ?? '';
    searchValue.current = inputText;
    if (delayDebounceFn.current) {
      clearTimeout(delayDebounceFn.current);
    }
    delayDebounceFn.current = setTimeout(() => {
      if (showContent) childRef.current.search(inputText);
      if (shouldContentShow() && !showContent) showPopup();
    }, searchTimeout);
  };

  const handleSearchKeyPress = ({ event }: { event?: KeyboardEvent }) => {
    switch (event?.code) {
    case 'ArrowDown': {
      if (showContent) {
        childRef.current.moveDown();
      } else {
        showPopup();
      }
      break;
    }
    case 'ArrowUp': {
      if (showContent) {
        childRef.current.moveUp();
        event.preventDefault();
      }
      break;
    }
    case 'Tab': {
      if (showContent) {
        childRef.current.focus();
        event.preventDefault();
      }
      break;
    }
    case 'Enter': {
      if (showContent) {
        childRef.current.selectCurrent();
      } else {
        showPopup();
      }
      break;
    }
    default:
    }
  };

  const handleDropdownButtonClick = () => {
    if (showContent) {
      textBoxRef.current!.instance.blur();
      popupRef.current!.instance.hide();
    } else {
      !props?.readOnly && showPopup();
    }
  };

  const handleDetailButtonClick = () => props?.onDetailButton?.();

  const handleOpenButtonClick = () => {
    props.onOpenDocButton?.();
  };

  const handlePopupOutsideClick = (e: Event) => {
    const textBoxInstance = textBoxRef.current!.instance;
    return !textBoxInstance.element().contains(e.target as Node);
  };

  const handlePopupShowing = () => {
    setShowContent(true);
  };

  const handlePopupHiding = () => {
    setShowContent(false);
    leaveSearch();
  };

  const handlePopupShown = () => {
    if (useAdditionalSearch) {
      popupTextBoxRef.current!.instance.focus();
      popupTextBoxRef.current!.instance.option('value', searchValue.current);
    }
  };

  const handleTextBoxFocusOut = () => {
    if (!showContent) leaveSearch();
  };

  const handleTextBoxFocusIn = () => {
    childRef.current.loadData();
    if (!props?.readOnly) (useAdditionalSearch || shouldContentShow()) ? showPopup() : enterSearch();
  };

  const textBoxProps = Object.fromEntries(
    Object.entries(props).filter(([ key ]) => ![ 'value' ].includes(key))
  );

  return (
    <>
      <TextBox
        {...textBoxProps}
        ref={textBoxRef}
        id={textBoxId.current}
        className={useAdditionalSearch ? styles.mainTextboxInactive : undefined}
        hoverStateEnabled
        placeholder={placeholder}
        onInput={(e) => handleSearchTextChange(e as { event?: Event & { target: HTMLInputElement } })}
        readOnly={(useAdditionalSearch && showContent) || props.readOnly}
        onFocusOut={handleTextBoxFocusOut}
        onFocusIn={handleTextBoxFocusIn}
        onKeyDown={(e) => handleSearchKeyPress(e as { event?: KeyboardEvent })}
        width={width}
        onValueChanged={() => {
          if (showContent) textBoxRef.current?.instance.option('isValid', true);
        }}
      >
        {!useAdditionalSearch && props.validator}

        {!readOnly && props.onEdit && text &&
          <TextBoxButton name='editButton' location='before'
            options={{
              disabled: false,
              icon: 'edit',
              focusStateEnabled: false,
              onClick: () => (props.onEdit as (v: unknown) => void)(props.value),
            }}
          />
        }
        <TextBoxButton name='clear' location='after'/>
        <TextBoxButton name='sdButton' location='after'
          options={{
            disabled: false,
            icon: useAdditionalSearch ? 'search' : showContent ? 'spinup' : 'spindown',
            activeStateEnabled: !useAdditionalSearch,
            focusStateEnabled: false,
            hoverStateEnabled: !useAdditionalSearch,
            onClick: handleDropdownButtonClick,
          }}
        />
        {props?.onDetailButton &&
          <TextBoxButton name='DatailButton' location='after'
            options={{
              icon: 'detailslayout',
              hint: 'Деталізація',
              focusStateEnabled: false,
              onClick: handleDetailButtonClick,
            }}
          />
        }
        {props?.onOpenDocButton &&
          <TextBoxButton name='openDocButton' location='after'
            options={{
              icon: 'doc',
              activeStateEnabled: !useAdditionalSearch,
              focusStateEnabled: false,
              hoverStateEnabled: !useAdditionalSearch,
              onClick: handleOpenButtonClick,
            }}
          />
        }
      </TextBox>
      {props?.detailComponent && props.detailComponent}
      <Popup
        ref={popupRef}
        dragEnabled={popupMode === 'popup'}
        width={dropdownWidth}
        maxWidth={isXSmall ? '100%' : 'auto'}
        height={popupMode === 'dropdown' ? dropdownHeight : '80%'}
        shading={false}
        showTitle={useAdditionalSearch}
        focusStateEnabled={false}
        closeOnOutsideClick={handlePopupOutsideClick}
        onHiding={handlePopupHiding}
        onShowing={handlePopupShowing}
        onShown={handlePopupShown}
        fullScreen={popupMode === 'fullscreen'}
        titleComponent={() => (
          <div className={styles.popupTitle}>
            <TextBox
              ref={popupTextBoxRef}
              stylingMode='filled'
              placeholder={placeholder}
              onInput={(e) => handleSearchTextChange(e as { event?: Event & { target: HTMLInputElement } })}
              onKeyDown={(e) => handleSearchKeyPress(e as { event?: KeyboardEvent })}
              showClearButton
              width='50%'
            />
            <Button icon='close' onClick={() => popupRef.current?.instance.hide()}/>
          </div>
        )}
      >
        {popupMode === 'dropdown' &&
          <Position
            my={props.top ? 'left bottom' : 'left top'}
            at={props.top ? 'left top' : 'left bottom'}
            of={`#${textBoxId.current}`}
          />
        }

        <div ref={popupContentRef} className={styles.popupContent}>
          {React.Children.only(
            React.cloneElement(
              props.children,
              {
                ref: childRef,
                value: props.value ?? null,
                onSelectionChanged: handleSelectionChange,
              },
            ),
          )}
        </div>
      </Popup>
    </>
  );
};

export const forwardProps = {
  minSearchLength: PropTypes.number,
  searchTimeout: PropTypes.number,
  placeholder: PropTypes.string,
  saveSearchValueOnLeave: PropTypes.bool,
  calcSearchValue: PropTypes.func,
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  dropdownWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  dropdownHeight: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  top: PropTypes.bool,
  popupMode: PropTypes.oneOf([ 'dropdown', 'fullscreen', 'popup' ]),
  validator: PropTypes.element,
};
